import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import env from '../../.env';
import moment from 'moment';
import api from '../../api';
import jwt from 'jsonwebtoken';

const state = {
  id_token: null,
  access_token: null,
  refresh_token: null,
  token_expires: null,
  cognito_user: null,
  user_pool: new CognitoUserPool({
    UserPoolId: env.UserPoolId,
    ClientId: env.ClientId,
  }),
};

const getters = {
  id_token(state) {
    return state.id_token;
  },
  access_token(state) {
    return state.access_token;
  },
  refresh_token(state) {
    return state.refresh_token;
  },
  token_expires(state) {
    return state.token_expires;
  },
  cognito_user(state) {
    return state.cognito_user;
  },
  user_pool(state) {
    return state.user_pool;
  },
};

const mutations = {
  setIDToken(state, payload) {
    if (env.name === 'dev') {
      console.log(payload);
    }
    state.id_token = payload;
  },
  setAccessToken(state, payload) {
    state.access_token = payload;
  },
  setRefreshToken(state, payload) {
    state.refresh_token = payload;
  },
  setTokenExpires(state, payload) {
    state.token_expires = moment(payload);
  },
  setCognitoUser(state, payload) {
    state.cognito_user = payload;
  },
};

const actions = {
  setTokens(context, session) {
    if (!session) console.error('Session invalid');
    const tokens = {
      id_token: session.getIdToken().getJwtToken(),
      access_token: session.getAccessToken().getJwtToken(),
      refresh_token: session.getRefreshToken(),
    };

    context.commit('setIDToken', tokens.id_token);
    context.commit('setAccessToken', tokens.access_token);
    context.commit('setRefreshToken', tokens.refresh_token);
    context.commit('setTokenExpires', session.accessToken.payload.exp * 1000);
  },
  setCognitoUser(context, Username) {
    const cognitoUser = new CognitoUser({
      Username,
      Pool: context.getters.user_pool,
    });
    context.commit('setCognitoUser', cognitoUser);
  },
  login(context, { Username, Password }) {
    return new Promise((resolve, reject) => {
      context.dispatch('setLoading', true);

      context.dispatch('setCognitoUser', Username);

      const authenticationDetails = new AuthenticationDetails({
        Username,
        Password,
      });

      context.getters.cognito_user.authenticateUser(authenticationDetails, {
        onSuccess: result => {
          context.dispatch('setLoading', false);
          resolve({ result, type: 'success' });
        },
        onFailure: err => {
          context.dispatch('setLoading', false);
          reject(err);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          context.dispatch('setLoading', false);
          resolve({ type: 'completeProfileChallenge' });
        },
      });
    });
  },
  loginWithAzureAD() {
    window.location.replace(
      `${env.CognitoDomain}/oauth2/authorize?response_type=code&identity_provider=AzureAD&client_id=${env.ClientId}&redirect_uri=${window.location.origin}/login/external_provider&scope=email+openid+profile`
    );
  },
  getTokensFromCode(context, code) {
    return new Promise((resolve, reject) => {
      context.dispatch('setLoading', true);
      api
        .getTokensFromCode(code)
        .then(({ data }) => {
          const { id_token, access_token, refresh_token } = data;
          const decoded = jwt.decode(id_token);
          const Username = decoded['cognito:username'];
          context.dispatch('setCognitoUser', Username);

          const IdToken = new CognitoIdToken({ IdToken: id_token });
          const AccessToken = new CognitoAccessToken({
            AccessToken: access_token,
          });
          const RefreshToken = new CognitoRefreshToken({
            RefreshToken: refresh_token,
          });

          const session = new CognitoUserSession({
            IdToken,
            AccessToken,
            RefreshToken,
          });
          context.getters.cognito_user.setSignInUserSession(session);

          resolve(session);
        })
        .catch(err => reject(err))
        .finally(() => context.dispatch('setLoading', false));
    });
  },
  completeProfileChallenge(context, { given_name, family_name, new_password }) {
    return new Promise((resolve, reject) => {
      context.dispatch('setLoading', true);

      const challenge_params = { given_name, family_name };

      context.getters.cognito_user.completeNewPasswordChallenge(
        new_password,
        challenge_params,
        {
          onSuccess: result => {
            context.dispatch('setLoading', false);
            resolve({ result, type: 'success' });
          },
          onFailure: err => {
            context.dispatch('setLoading', false);
            reject(err);
          },
        }
      );
    });
  },
  changePassword(context, { old_password, new_password }) {
    return new Promise((resolve, reject) => {
      context.dispatch('setLoading', true);

      context.getters.cognito_user.changePassword(
        old_password,
        new_password,
        (err, result) => {
          if (err) {
            reject(err);
            return;
          }
          resolve({ result, type: 'success' });
        }
      );
    });
  },
  getUserDetails(context) {
    return new Promise((resolve, reject) => {
      context.getters.cognito_user.getUserAttributes((err, result) => {
        if (err) {
          reject(err);
          return;
        }

        const attributes = {};
        result.forEach(r => {
          const key = r.getName();
          const value = r.getValue();
          attributes[key] = value;
        });

        resolve();
      });
    });
  },
  refreshToken(context) {
    return new Promise((resolve, reject) => {
      const refresh_token = context.getters.refresh_token;
      context.getters.cognito_user.refreshSession(
        refresh_token,
        (err, session) => {
          if (err) {
            reject(err);
          } else {
            context.dispatch('setTokens', session);
            resolve();
          }
        }
      );
    });
  },
  requestPasswordReset(context, Username) {
    return new Promise((resolve, reject) => {
      context.dispatch('setLoading', true);

      context.dispatch('setCognitoUser', Username);

      context.getters.cognito_user.forgotPassword({
        onSuccess: data => {
          context.dispatch('setLoading', false);
          resolve();
        },
        onFailure: err => {
          context.dispatch('setLoading', false);
          reject(err);
        },
      });
    });
  },
  resetPassword(context, { code, password }) {
    return new Promise((resolve, reject) => {
      context.dispatch('setLoading', true);

      context.getters.cognito_user.confirmPassword(code, password, {
        onSuccess: () => {
          context.dispatch('setLoading', false);
          resolve();
        },
        onFailure: err => {
          context.dispatch('setLoading', false);
          reject(err);
        },
      });
    });
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
